<script>
export default {
    // vue-meta evaluates page() through a watcher that cannot see <script setup> bindings, so
    // the lookup it needs happens here rather than in setup.
    page() {
        const channel =
            this.$store.state.channels.channels.find(
                item => item.id === this.$route.params.channelId
            ) || {}
        const channelTitle = channel.title || 'Channel'
        const title = `Schedule — ${channelTitle}`
        const description = channel.description || `Upcoming show schedule for ${channelTitle}.`
        const image = `${import.meta.env.VITE_CLIENT_URL}/frisky-social-share-image.png`
        const canonicalUrl = `https://frisky.fm${this.$route.path}`
        return {
            title,
            link: [{ rel: 'canonical', vmid: 'canonical', href: canonicalUrl }],
            meta: [
                { name: 'title', vmid: 'title', content: title },
                { name: 'description', vmid: 'description', content: description },

                { property: 'og:title', vmid: 'og:title', content: title },
                { property: 'og:description', vmid: 'og:description', content: description },
                { property: 'og:image', vmid: 'og:image', content: image },
                { property: 'og:type', vmid: 'og:type', content: 'website' },
                { property: 'og:url', vmid: 'og:url', content: canonicalUrl },
                { property: 'og:site_name', vmid: 'og:site_name', content: 'FRISKY' },

                { name: 'twitter:card', vmid: 'twitter:card', content: 'summary_large_image' },
                { name: 'twitter:title', vmid: 'twitter:title', content: title },
                { name: 'twitter:description', vmid: 'twitter:description', content: description },
                { name: 'twitter:image', vmid: 'twitter:image', content: image },
                { name: 'twitter:url', vmid: 'twitter:url', content: canonicalUrl },
            ],
        }
    },
}
</script>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router/composables'
import DataLayer from '@services/data-layer/index'
import ShowScheduleBlock from '@components/show-schedule-block'
import format from 'date-fns/format'
import addDays from 'date-fns/addDays'
import startOfToday from 'date-fns/startOfToday'

const DAYS_AHEAD = 7
const PLACEHOLDER_ROWS = 6

const route = useRoute()

const dates = ref([])
const activeDate = ref({})
const schedule = ref(
    Array.from(Array(PLACEHOLDER_ROWS), (_row, id) => ({
        id,
        show: {
            id,
            title: '',
            url: '',
            type: 'show',
            image: {},
            genre: [],
            artist_id: [],
            artist: [],
        },
    }))
)

const activeDateLabel = computed(() => activeDate.value.label || '')

const dateLabels = computed(() => dates.value.map(date => date.label))

const clientTimezoneLabel = computed(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'your local timezone'
)

function getChannelSchedule(date) {
    DataLayer.episodes
        .getByDate(route.params.channelId, date, addDays(date, 1))
        .then(episodes => {
            schedule.value = episodes.body
        })
}

function handleDateChange(label) {
    activeDate.value = dates.value.find(date => date.label === label)
}

watch(activeDate, date => {
    if (!date.date) return

    getChannelSchedule(date.date)
})

onMounted(() => {
    const today = startOfToday()

    dates.value = Array.from(Array(DAYS_AHEAD), (_day, offset) => {
        const date = addDays(today, offset)
        return { label: format(date, 'd MMM yyyy'), date }
    })

    activeDate.value = dates.value[0]
})
</script>

<template>
    <BaseContent>
        <BaseSection
            title="Schedule"
            padded
        >
            <div slot="right">
                <BaseCustomSelect
                    :options="dateLabels"
                    :active="activeDateLabel"
                    prefix="Date:"
                    @change="handleDateChange"
                />
            </div>
            <p class="timezone-note">
                Showing times in {{ clientTimezoneLabel }}
            </p>
            <ShowScheduleBlock
                :resources="schedule"
                :error="{ paragraph: 'There are no shows scheduled for this day' }"
            />
        </BaseSection>
    </BaseContent>
</template>

<style lang="scss" scoped>
@import '@design';

.timezone-note {
    @extend %contentSubtitleS;

    margin: 0 0 $s-m;
    color: $c-shuttle-gray;
}
</style>
